import { Video, VideoProps } from "@modernatx-internal/component-library";
import Hls from "hls.js";
import React from "react";

import { useExperience } from "@/context/ExperienceContext";

export interface VideoData {
  pictures?: {
    src: string;
  }[];
  tracks?: {
    src: string;
    label: string;
    lang: string;
    kind?: string;
  }[];
  videoUrl?: string;
}

export interface StreamingVideoProps extends Omit<VideoProps, "ref" | "sources"> {
  streamingPath: string;
  videoData?: VideoData;
  sources?: VideoProps["sources"];
}

type VideoTextTrack = NonNullable<VideoProps["textTracks"]>[number];

export const StreamingVideo: React.FC<StreamingVideoProps> = ({ videoData, ...props }) => {
  const { screenshot } = useExperience();
  const hlsPlayer = React.useRef<Hls | null>(null);
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [, triggerRefresh] = React.useState({});
  const aspectRatio = props.aspectRatio ?? "16 / 9";

  React.useEffect(() => {
    if (videoData?.videoUrl && videoRef.current && Hls.isSupported()) {
      hlsPlayer.current = hlsPlayer.current || new Hls();
      hlsPlayer.current.attachMedia(videoRef.current);
      hlsPlayer.current.loadSource(videoData.videoUrl);
    }

    const videoEl = videoRef.current;
    const preventPrematurePlayedState = (e: Event) => {
      // Hls.js triggers a timeupdate at 0 when loading, causing the Component Library to set 'played' to true
      // and hide the controls. We prevent this event from reaching the Component Library if we haven't started playing.
      if (e.target instanceof HTMLVideoElement && e.target.currentTime === 0 && e.target.paused) {
        e.stopImmediatePropagation();
      }
    };

    const onDurationChange = () => {
      triggerRefresh({});
    };

    videoEl?.addEventListener("timeupdate", preventPrematurePlayedState, { capture: true });
    videoEl?.addEventListener("durationchange", onDurationChange);

    return () => {
      videoEl?.removeEventListener("timeupdate", preventPrematurePlayedState, { capture: true });
      videoEl?.removeEventListener("durationchange", onDurationChange);
      if (hlsPlayer.current) {
        hlsPlayer.current.destroy();
        hlsPlayer.current = null;
      }
    };
  }, [videoData]);

  if (!videoData) {
    return <Video ref={videoRef} sources={[]} {...props} aspectRatio={aspectRatio} />;
  }

  return (
    <Video
      ref={videoRef}
      crossOrigin="anonymous"
      sources={[]}
      {...props}
      aspectRatio={aspectRatio}
      poster={videoData.pictures?.[0]?.src || props.poster}
      stickyControls={!screenshot}
      textTracks={[
        ...(props.textTracks || []),
        ...(videoData.tracks?.map((track) => {
          let kind = track.kind;
          if (
            !kind &&
            (track.label?.toLowerCase() === "chapters" ||
              track.src?.includes("_video_text_track__c"))
          ) {
            kind = "chapters";
          }
          return {
            ...track,
            kind: (kind || "subtitles") as VideoTextTrack["kind"],
            label: track.label?.replace("(auto-generated)", "")
          };
        }) || [])
      ]}
    />
  );
};
